/**
 * When a patient may take the symptom assessment for an appointment.
 *
 * Shared so the home page's appointment cards and the symptom chart's button
 * cannot disagree — they were previously two separate rules, and only one of
 * them was reachable for a diagnosis the patient had never been treated for.
 *
 * The server enforces the same rule (QuestionnaireRules in the backend) and has
 * the last word; this only decides whether to offer the button. Keep the two,
 * and the mobile port, in step.
 */

/** How far ahead of a visit the assessment opens. */
export const ASSESSMENT_WINDOW_DAYS = 7;

const toDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Missed and cancelled visits never happened, so they don't make a later
// appointment a follow-up, and a follow-up doesn't wait for them.
const WAS_NOT_SEEN = ['CANCELED', 'UNATTENDED'];

/**
 * The moment the assessment for this appointment opens: midnight, the patient's
 * own time, a week before the visit. A follow-up asks how things have changed
 * since the last visit, so if an earlier appointment for the same condition is
 * still to come, it opens when that one ends instead.
 */
export const assessmentOpensAt = (appt, others = []) => {
  const start = toDate(appt?.apptStartTime);
  if (!start) return null;

  const opens = new Date(start);
  opens.setHours(0, 0, 0, 0);
  opens.setDate(opens.getDate() - ASSESSMENT_WINDOW_DAYS);

  let latest = opens;
  for (const other of others || []) {
    if (!other || other.apptId === appt.apptId) continue;
    if (Number(other.diagId) !== Number(appt.diagId)) continue;
    if (WAS_NOT_SEEN.includes(other.status)) continue;
    const otherStart = toDate(other.apptStartTime);
    if (!otherStart || otherStart >= start) continue;
    const otherEnd = toDate(other.apptEndTime) || otherStart;
    if (otherEnd > latest) latest = otherEnd;
  }
  return latest;
};

/**
 * Open from midnight a week before the visit until the visit starts. A patient
 * with a 3pm appointment can take it until 3pm that day, not after.
 *
 * @param others the patient's other appointments, so a follow-up can wait for
 *               an earlier visit that hasn't happened yet.
 */
export const isWithinAssessmentWindow = (appt, others = [], now = new Date()) => {
  if (!appt || appt.status === 'CANCELED') return false;

  const start = toDate(appt.apptStartTime);
  const opens = assessmentOpensAt(appt, others);
  if (!start || !opens) return false;

  return now >= opens && now < start;
};

/**
 * Whether this appointment already has an assessment.
 *
 * The appointment carries its own score, resolved server-side by appointment id
 * (AppointmentMapper.getSymptomScore -> findByApptId), and is left at 0 when
 * none exists. That is the authoritative per-visit answer; inferring it from how
 * recently any score was taken locked out patients returning for a condition
 * they had been assessed for before.
 */
export const hasAssessmentForAppt = (appt) => Number(appt?.symptomScore ?? 0) !== 0;

/** An appointment the patient can assess right now. */
export const canAssessAppt = (appt, others = [], now = new Date()) =>
  isWithinAssessmentWindow(appt, others, now) && !hasAssessmentForAppt(appt);
